import { useQuery } from '@tanstack/react-query'
import { HardDrive, Package, FolderTree, Trash2, RefreshCw } from 'lucide-react'
import Card, { CardHeader } from '../components/Card'
import Button from '../components/Button'
import { getStorageStats, getManifests, getTinyVols } from '../lib/api'
import type { Manifest } from '../types'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function Storage() {
  const { data: stats, isLoading: _statsLoading } = useQuery({
    queryKey: ['storageStats'],
    queryFn: getStorageStats,
    refetchInterval: 30000,
  })

  const { data: manifests = [], isLoading: manifestsLoading } = useQuery({
    queryKey: ['manifests'],
    queryFn: getManifests,
  })

  const { data: tinyvols = [], isLoading: tinyvolsLoading } = useQuery({
    queryKey: ['tinyvols'],
    queryFn: getTinyVols,
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Storage</h1>
          <p className="mt-1 text-gray-500">Manage CAS blobs, manifests, and TinyVols</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="secondary" icon={<RefreshCw className="h-4 w-4" />}>
            Refresh
          </Button>
          {/* Orphaned-blob cleanup is not implemented on the backend yet, so
              the control is disabled rather than triggering a no-op that
              looks like it succeeded. */}
          <Button
            variant="danger"
            disabled
            title="Not implemented yet"
            icon={<Trash2 className="h-4 w-4" />}
          >
            Cleanup Orphans (soon)
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <HardDrive className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">CAS Blobs</p>
              <p className="text-2xl font-bold">{stats?.total_blobs ?? '-'}</p>
              <p className="text-xs text-gray-500">
                {stats ? formatBytes(stats.total_size) : '-'}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-voltainer-100 rounded-lg">
              <Package className="h-6 w-6 text-voltainer-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Manifests</p>
              <p className="text-2xl font-bold">{stats?.manifest_count ?? '-'}</p>
              <p className="text-xs text-gray-500">Container definitions</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-armored-100 rounded-lg">
              <FolderTree className="h-6 w-6 text-armored-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">TinyVols</p>
              <p className="text-2xl font-bold">{stats?.tinyvol_count ?? '-'}</p>
              <p className="text-xs text-gray-500">Assembled rootfs</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Manifests */}
      <Card className="mb-6">
        <CardHeader title="Manifests" description="Container image manifests from ArmoredForge" />
        {manifestsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-voltainer-600" />
          </div>
        ) : manifests.length === 0 ? (
          <div className="text-center py-8">
            <Package className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-gray-500">No manifests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-500">ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Version</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Files</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Signed</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Created</th>
                </tr>
              </thead>
              <tbody>
                {manifests.map((manifest: Manifest) => (
                  <tr key={manifest.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-xs">{manifest.id}</td>
                    <td className="py-3 px-4">{manifest.version || '-'}</td>
                    <td className="py-3 px-4">{manifest.file_count}</td>
                    <td className="py-3 px-4">
                      {manifest.signed ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          ✓ Signed
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          Unsigned
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500">
                      {manifest.created_at ? formatDate(manifest.created_at) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* TinyVols */}
      <Card>
        <CardHeader title="TinyVols" description="Assembled container root filesystems" />
        {tinyvolsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-armored-600" />
          </div>
        ) : tinyvols.length === 0 ? (
          <div className="text-center py-8">
            <FolderTree className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-gray-500">No TinyVols found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-500">ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Files</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Size</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Modified</th>
                </tr>
              </thead>
              <tbody>
                {tinyvols.map((tv) => (
                  <tr key={tv.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-xs">{tv.id}</td>
                    <td className="py-3 px-4">{tv.file_count}</td>
                    <td className="py-3 px-4">{formatBytes(tv.size)}</td>
                    <td className="py-3 px-4 text-gray-500">
                      {tv.modified_at ? formatDate(tv.modified_at) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
